import { PayloadAction } from '@reduxjs/toolkit'
import { Draft } from 'immer'
import { ContentItem, Video } from 'interfaces'
import { BRAND_LINKEDIN } from 'shared'
import {
  BRAND_BLUESKY,
  BRAND_FACEBOOK,
  BRAND_GOOGLE,
  BRAND_INSTAGRAM,
  BRAND_PINTEREST,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  NETWORKS_NAMES,
  NETWORKS_ORDERED,
  STOCK_VIDEO_TYPE,
  TIKTOK_ALLOWED_IMAGE_EXTENSIONS,
  TIKTOK_ALLOWED_VIDEO_FILE_EXTENSIONS
} from 'shared/constants'
import {
  COMPOSER_STATUS_KEY_GENERIC,
  ComposerBlueskyPost,
  ComposerFacebookPost,
  ComposerGooglePost,
  ComposerInstagramPost,
  ComposerLinkedinPost,
  ComposerPinterestPost,
  ComposerThreadsPost,
  ComposerTikTokPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  FBAlbum,
  GOOGLE_POST_TYPE_UPDATE,
  LocationInfo,
  PINTEREST_AI_SYNTHETIC_PERFORMER,
  PinterestBoard,
  PostComment, PostDestination, PostDestinationType, PrivacyStatus, TikTokPostProfileSettings, TweetType
} from 'shared/types'
import { getFileExtensionFromUrl } from 'shared/utils'
import { generateBulkPostCaptionFromHtml } from 'utils/composer'
import { ArticleBase } from '../interfaces/ArticleBase'
import ComposerState, {
  BulkContentPost, defaultGoogleEventEndTime, defaultScheduleTimeString, PostNetworkData
} from '../state'
import { AI_CONTENT_NETWORKS, randomId, toRememberedCommentDelay } from '../utils'

export const PINTEREST_MULTI_ACCOUNT_ERROR_CODE = 'composer.errors.pinterest.multi-account'
export const PINTEREST_RECYCLE_ERROR_CODE = 'composer.errors.pinterest.recycle'
export const BLUESKY_PHOTO_SIZE_ERROR_CODE = 'composer.errors.bluesky.photo-size'
export const YOUTUBE_TITLE_TOO_LONG_ERROR_CODE = 'composer.errors.youtube.post-length'
const PINTEREST_RECYCLE_ERROR_MESSAGE = 'Pinterest does not allow Post Planner to recycle or repeat posts.'
const TIKTOK_PRIVACY_ERROR_CODE = 'composer.errors.tiktok.privacy'
const TIKTOK_PROMOTION_ERROR_CODE = 'composer.errors.tiktok.promotion'
const TIKTOK_SETTINGS_ERROR_CODES = [TIKTOK_PRIVACY_ERROR_CODE, TIKTOK_PROMOTION_ERROR_CODE]

/** TikTok requires both to be picked explicitly - the same rule `composerTiktokPostStatusSelector` applies. */
function tikTokSettingsError(settings: TikTokPostProfileSettings) {
  if (!settings.privacyLevel) {
    return { code: TIKTOK_PRIVACY_ERROR_CODE, message: 'Please choose a video privacy level.' }
  }
  if (!settings.promoting) {
    return { code: TIKTOK_PROMOTION_ERROR_CODE, message: 'Please choose a video promotion setting.' }
  }
  return undefined
}

/** Only ever writes over its own error, so an unrelated one (media, upload) is neither hidden nor cleared. */
function applyTikTokSettingsError(post: Draft<BulkContentPost>, tkData: ComposerTikTokPost) {
  const ownsError = !post.error || TIKTOK_SETTINGS_ERROR_CODES.includes(post.error.code)
  if (!ownsError) {
    return
  }
  post.error = Object.values(tkData.settings).map(tikTokSettingsError).find(Boolean)
}

export const multipostReducers = {
  addImagePosts: (state: Draft<ComposerState>, action: PayloadAction<Array<{ url: string, content?: ContentItem }>>) => {
    let count = Object.keys(state.multiPost.posts).length
    NETWORKS_ORDERED.forEach((network: PostDestinationType) => {
      action.payload.forEach((image) => {
        count += 1
        setInitialPostState({
          draft: state.multiPost.posts,
          order: count,
          network,
          igLocations: state.posts.instagram.locations,
          image
        })
      })
    })

    state.images = {}
    state.contentItem = undefined
  },
  addImageUploadPosts: (state: Draft<ComposerState>, action: PayloadAction<Record<string, { file: File }>>) => {
    let count = Object.keys(state.multiPost.posts).length
    NETWORKS_ORDERED.forEach((network: PostDestinationType) => {
      Object.keys(action.payload).forEach((fileId: string) => {
        const file = action.payload[fileId].file
        count += 1
        setInitialPostState({ draft: state.multiPost.posts, order: count, network, igLocations: state.posts.instagram.locations, file })
      })
    })
    state.images = {}
    state.contentItem = undefined
  },
  addArticlePosts: (state: Draft<ComposerState>, action: PayloadAction<Array<{ article: ArticleBase, content?: ContentItem }>>) => {
    let count = Object.keys(state.multiPost.posts).length
    NETWORKS_ORDERED.forEach((network: PostDestinationType) => {
      action.payload.forEach((data) => {
        count += 1
        setInitialPostState({
          draft: state.multiPost.posts,
          order: count,
          network,
          igLocations: state.posts.instagram.locations,
          article: data
        })
      })
    })

    state.images = {}
    state.contentItem = undefined
  },
  addVideoPosts: (state: Draft<ComposerState>, action: PayloadAction<Array<ContentItem>>) => {
    let count = Object.keys(state.multiPost.posts).length
    NETWORKS_ORDERED.forEach((network: PostDestinationType) => {
      action.payload.forEach((video) => {
        count += 1
        setInitialPostState({ draft: state.multiPost.posts, order: count, network, igLocations: state.posts.instagram.locations, video })
      })
    })

    state.images = {}
    state.contentItem = undefined
  },
  addMultipostVideoFiles: (
    state: Draft<ComposerState>,
    action: PayloadAction<Array<{ url: string, name?: string, thumbnailUrl?: string }>>
  ) => {
    let count = Object.keys(state.multiPost.posts).length
    NETWORKS_ORDERED.forEach((network: PostDestinationType) => {
      action.payload.forEach((videoFile) => {
        count += 1
        setInitialPostState({
          draft: state.multiPost.posts,
          order: count,
          network,
          igLocations: state.posts.instagram.locations,
          videoFile
        })
      })
    })

    state.images = {}
    state.contentItem = undefined
  },
  imagesUploadDone: (
    state: Draft<ComposerState>,
    action: PayloadAction<Record<string, { file: File, url?: string, error?: string }>>
  ) => {
    const photos = action.payload
    Object.values(state.multiPost.posts).forEach((post) => {
      if (post.imageUpload) {
        const upload = photos[post.imageUpload.file.name]
        post.imageUpload = upload
        post.imageUrl = upload.url
        post.error = upload.error ? { message: upload.error, code: '' } : post.error
      }
    })
  },
  imageUploadUpdate: (state: Draft<ComposerState>, action: PayloadAction<{ fileName: string, error?: string, src?: string }>) => {
    const { fileName, error, src } = action.payload
    Object.values(state.multiPost.posts).forEach((post) => {
      if (post.imageUpload?.file.name === fileName) {
        post.imageUpload.error = error
        post.imageUpload.src = src
        post.imageUrl = undefined
        post.error = error ? { message: error, code: '' } : post.error
      }
    })
  },
  removeMultiPosts: (state: Draft<ComposerState>, action: PayloadAction<{ ids: string[] }>) => {
    action.payload.ids.forEach((id) => {
      delete state.multiPost.posts[id]
    })
  },
  reorderMultiPosts: (state: Draft<ComposerState>, action: PayloadAction<string[]>) => {
    action.payload.forEach((id, index) => {
      const post = state.multiPost.posts[id]
      if (post) {
        post.order = index + 1
      }
    })
  },
  editMultiPosts: (
    state: Draft<ComposerState>,
    action: PayloadAction<{
      id: string,
      network: PostDestinationType,
      data: Partial<PostNetworkData> & { applyToAll?: boolean },
      imageUrl?: string,
      recycle?: boolean | Date | number
    }>
  ) => {
    const post = state.multiPost.posts[action.payload.id]
    if (!post) {
      return
    }
    const { applyToAll, ...networkData } = action.payload.data
    const withStatusEdit = action.payload.data?.status?.text || (networkData as any)?.description
    if (withStatusEdit) {
      state.useGenericStatus = false
    }
    if (action.payload.imageUrl) {
      post.imageUrl = action.payload.imageUrl
    }
    if (action.payload.recycle !== undefined) {
      post.recycle = action.payload.recycle
      if (post.network === BRAND_PINTEREST && !post.error) {
        post.error = {
          code: PINTEREST_RECYCLE_ERROR_CODE,
          message: PINTEREST_RECYCLE_ERROR_MESSAGE
        }
      }

      if (action.payload.recycle === false && post.network === BRAND_PINTEREST && post.error?.code === PINTEREST_RECYCLE_ERROR_CODE) {
        // remove recycle error if recycle is being unset
        post.error = undefined
      }
    }

    if (networkData) {
      post.data = { ...post.data, ...networkData }
    }

    Object.values(state.multiPost.posts).forEach((p) => {
      if (p.id !== action.payload.id && applyToAll && networkData) {
        if (withStatusEdit) {
          p.data.status = { html: '', text: withStatusEdit, touched: false }
        } else if (p.network === post.network) {
          // Non-status fields (e.g. title) collide across networks, so "apply to all" stays within the network.
          p.data = { ...p.data, ...networkData }
        }
      }
    })
  },
  handleMultiPostError: (state: Draft<ComposerState>, action: PayloadAction<Array<{ frontendId: string, error: string }>>) => {
    const errorsMap = action.payload.reduce((map: Record<string, string>, e) => {
      map[e.frontendId] = e.error
      return map
    }, {})

    Object.keys(state.multiPost.posts).forEach((id) => {
      if (errorsMap[id]) {
        state.multiPost.posts[id].error = { message: errorsMap[id], code: '' }
      } else {
        delete state.multiPost.posts[id]
      }
    })
  },
  setMultiPostError: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, error: { code: string, message: string } | undefined }>
  ) => {
    const post = state.multiPost.posts[action.payload.postId]
    // Async validations (e.g. image size checks) can resolve after the post was deleted.
    if (post) {
      post.error = action.payload.error
    }
  },
  setMultiPostsRecycle: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postIds: string[], value: Date | boolean | number, applyToAll?: boolean }>
  ) => {
    const { postIds, value, applyToAll } = action.payload
    if (applyToAll) {
      Object.keys(state.multiPost.posts).forEach((id) => {
        const post = state.multiPost.posts[id]
        post.recycle = value

        if (post.network === BRAND_PINTEREST) {
          if (!post.error && Boolean(value)) {
            post.error = {
              code: PINTEREST_RECYCLE_ERROR_CODE,
              message: PINTEREST_RECYCLE_ERROR_MESSAGE
            }
          }

          if (value === false && post.error?.code === PINTEREST_RECYCLE_ERROR_CODE) {
            post.error = undefined
          }
        }
      })
    } else {
      postIds.forEach((id) => {
        const post = state.multiPost.posts[id]
        if (!post) {
          return
        }
        post.recycle = value
        if (post.network === BRAND_PINTEREST && !post.error) {
          post.error = {
            code: PINTEREST_RECYCLE_ERROR_CODE,
            message: PINTEREST_RECYCLE_ERROR_MESSAGE
          }
        }
        if (value === false && post.network === BRAND_PINTEREST && post.error?.code === PINTEREST_RECYCLE_ERROR_CODE) {
          post.error = undefined
        }
      })
    }
  },
  applyGenericStatusToPosts: (state: Draft<ComposerState>, action: PayloadAction<string[]>) => {
    const genericStatusHtml = state.posts[COMPOSER_STATUS_KEY_GENERIC].status.html
    action.payload.forEach((id) => {
      const post = state.multiPost.posts[id]
      if (!post) {
        return
      }
      const { html, text } = generateBulkPostCaptionFromHtml(genericStatusHtml, post)
      post.data.status = {
        html,
        text,
        touched: false
      }

      if ([BRAND_PINTEREST, BRAND_YOUTUBE, BRAND_TIKTOK].includes(post.network)) {
        (post.data as ComposerPinterestPost).description = text
      }
    })
  },
  resetMultiPostState: (state: Draft<ComposerState>) => {
    state.multiPost = { posts: {} }
  },
  setMultiPostPiBoards: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, selection: Record<string, PinterestBoard>, applyToAll?: boolean }>
  ) => {
    const { postId, selection, applyToAll } = action.payload
    const selectionBoardsArray = Object.keys(selection).map(ppid => ({ ...selection[ppid], ppid }))
    if (applyToAll) {
      Object.values(state.multiPost.posts).forEach(post => {
        if (post.network === BRAND_PINTEREST) {
          const current = (post.data as ComposerPinterestPost).boards || []
          const next = current.filter((b) => !selectionBoardsArray.find((sb) => sb.ppid === b.ppid)).concat(selectionBoardsArray)
            ; (post.data as ComposerPinterestPost).boards = next
        }
      })
    } else {
      const post = state.multiPost.posts[postId]
      if (post) {
        const currentBoards = (post.data as ComposerPinterestPost).boards || []
        const next = currentBoards.filter((b) => !selectionBoardsArray.find((sb) => sb.ppid === b.ppid)).concat(selectionBoardsArray)
          ; (post.data as ComposerPinterestPost).boards = next
      }
    }
  },
  setMultiPostIGLocations: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, locations: Record<string, LocationInfo | undefined>, applyToAll: boolean }>
  ) => {
    const { postId, locations, applyToAll } = action.payload
    if (applyToAll) {
      Object.values(state.multiPost.posts).forEach(post => {
        if (post.network === BRAND_INSTAGRAM) {
          (post.data as ComposerInstagramPost).locations = locations
        }
      })
    } else {
      const post = state.multiPost.posts[postId]
      if (post) {
        (post.data as ComposerInstagramPost).locations = locations
      }
    }
  },
  setMultiPostFBAlbums: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, albums: Record<string, FBAlbum | undefined>, applyToAll: boolean }>
  ) => {
    const { postId, albums, applyToAll } = action.payload
    const albumsArr = Object.values(albums).filter(Boolean) as FBAlbum[]
    if (applyToAll) {
      Object.values(state.multiPost.posts).forEach(post => {
        if (post.network === BRAND_FACEBOOK) {
          (post.data as ComposerFacebookPost).albums = albumsArr
        }
      })
    } else {
      const post = state.multiPost.posts[postId]
      if (post) {
        (post.data as ComposerFacebookPost).albums = albumsArr
      }
    }
  },
  setMultiPostFBReshareAll: (state: Draft<ComposerState>, action: PayloadAction<boolean>) => {
    Object.values(state.multiPost.posts).forEach(post => {
      if (post.network === BRAND_FACEBOOK && post.content?.feed.type === BRAND_FACEBOOK) {
        (post.data as ComposerFacebookPost).reshare = action.payload
      }
    })
  },
  setMultiPostTweetTypeAll: (state: Draft<ComposerState>, action: PayloadAction<TweetType>) => {
    Object.values(state.multiPost.posts).forEach(post => {
      if (post.network === BRAND_TWITTER && post.content?.feed.type === BRAND_TWITTER) {
        (post.data as ComposerTwitterPost).tweetType = action.payload
      }
    })
  },
  setMultiPostNetworkSettings: (
    state: Draft<ComposerState>,
    action: PayloadAction<{
      postIds: string[],
      fbAlbums: Record<string, FBAlbum | undefined>,
      piBoards: Record<string, PinterestBoard>,
      igLocations: Record<string, LocationInfo | undefined>,
      tkSettings: Record<string, TikTokPostProfileSettings>,
      ytPrivacy: PrivacyStatus,
      fbPostType: string | undefined,
      igPostType: string | undefined,
      /** `undefined` when the popup's AI content checkbox was left untouched - the posts keep their own flags. */
      aiContent?: boolean,
      /** `undefined` when the popup's Pinterest AI-generated person checkbox was left untouched - the pins keep their own mode. */
      piSyntheticPerformer?: boolean
    }>
  ) => {
    const {
      postIds, fbAlbums, piBoards, igLocations, tkSettings, ytPrivacy, fbPostType, igPostType, aiContent, piSyntheticPerformer
    } = action.payload
    const albumsArr = Object.values(fbAlbums).filter(Boolean) as FBAlbum[]
    const boardsArray = Object.keys(piBoards).map(ppid => ({ ...piBoards[ppid], ppid }))

    postIds.forEach(id => {
      const post = state.multiPost.posts[id]
      if (!post) {
        return
      }
      if (aiContent !== undefined && AI_CONTENT_NETWORKS.includes(post.network)) {
        post.isAiContent = aiContent
      }
      switch (post.network) {
        case BRAND_FACEBOOK:
          (post.data as ComposerFacebookPost).albums = albumsArr
          ; (post.data as ComposerFacebookPost).postType = fbPostType
          break
        case BRAND_INSTAGRAM:
          (post.data as ComposerInstagramPost).locations = igLocations
          ; (post.data as ComposerInstagramPost).postType = igPostType
          break
        case BRAND_PINTEREST:
          (post.data as ComposerPinterestPost).boards = boardsArray
          if (piSyntheticPerformer !== undefined) {
            (post.data as ComposerPinterestPost).aiContentMode = piSyntheticPerformer ? PINTEREST_AI_SYNTHETIC_PERFORMER : undefined
          }
          break
        case BRAND_TIKTOK:
          const tkData = post.data as ComposerTikTokPost
          tkData.settings = { ...tkData.settings, ...tkSettings }
          break
        case BRAND_YOUTUBE:
          (post.data as ComposerYoutubePost).privacyStatus = ytPrivacy
          break
      }
    })
  },
  setMultiPostAiContent: (state: Draft<ComposerState>, action: PayloadAction<{ postIds: string[], value: boolean }>) => {
    action.payload.postIds.forEach(id => {
      const post = state.multiPost.posts[id]
      if (post) {
        post.isAiContent = action.payload.value
      }
    })
  },
  setMultipostTikTokProfileSettings: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, settings: TikTokPostProfileSettings }>
  ) => {
    const { postId, settings } = action.payload
    const post = state.multiPost.posts[postId]
    if (!post) {
      return
    }
    const tkData = post.data as ComposerTikTokPost
    tkData.settings = { ...tkData.settings, [settings.ppid]: settings }
    applyTikTokSettingsError(post, tkData)
  },
  initializeTikTokSettings: (state: Draft<ComposerState>, action: PayloadAction<Record<string, TikTokPostProfileSettings>>) => {
    const settings = action.payload
    Object.values(state.multiPost.posts).forEach(post => {
      if (post.network === BRAND_TIKTOK) {
        const tkData = post.data as ComposerTikTokPost
        tkData.settings = { ...tkData.settings, ...settings }
        // The fetched settings carry no privacy level until the user picks one. Nothing else validates the bulk
        // path, so without this the post is queued with an empty privacy level and no error is ever shown.
        applyTikTokSettingsError(post, tkData)
      }
    })
  },
  setMultipostComment: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, comment: Partial<PostComment> }>
  ) => {
    const { postId, comment } = action.payload
    const post = state.multiPost.posts[postId]
    if (!post) {
      return
    }
    const comments = (post.data as ComposerFacebookPost).comments

    const index = comments.findIndex(c => c.id === comment.id)
    if (index !== -1) {
      Object.entries(comment).forEach(([key, value]) => {
        if (value !== undefined) {
          (comments[index] as any)[key] = value
        }
      })
    }

  },
  addMultiPostComment: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string }>
  ) => {
    const { postId } = action.payload
    const post = state.multiPost.posts[postId]
    if (!post) {
      return
    }
    const comments = (post.data as ComposerFacebookPost).comments
    comments.push({ id: randomId(), text: '', delay: state.lastCommentDelay, html: '' })
  },
  copyMultiPostCommentToAll: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, commentId: string }>
  ) => {
    const { postId, commentId } = action.payload
    const post = state.multiPost.posts[postId]
    if (!post) {
      return
    }
    const source = (post.data as ComposerFacebookPost).comments.find(c => c.id === commentId)
    const text = source?.text.trim()
    if (!source || !text) {
      return
    }

    Object.values(state.multiPost.posts).forEach((p) => {
      if (p.id === postId || p.network !== post.network) {
        return
      }
      const data = p.data as ComposerFacebookPost
      if (data.comments.some(c => c.text.trim() === text)) {
        return
      }
      // Blank comments are just empty slots (every post starts with one), so the copy takes their place.
      data.comments = data.comments.filter(c => c.text.trim())
      data.comments.push({ id: randomId(), text: source.text, html: source.html, delay: source.delay })
    })
  },
  setMultipostCommentDelay: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, commentId: string, delay: number }>
  ) => {
    const { postId, commentId, delay } = action.payload
    const post = state.multiPost.posts[postId]
    if (!post) {
      return
    }
    const comment = (post.data as ComposerFacebookPost).comments.find(c => c.id === commentId)
    if (comment) {
      comment.delay = delay
    }
    state.lastCommentDelay = toRememberedCommentDelay(delay)
  },
  removeMultipostComment: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, commentId: string }>
  ) => {
    const { postId, commentId } = action.payload
    const post = state.multiPost.posts[postId]
    if (!post) {
      return
    }
    const comments = (post.data as ComposerFacebookPost).comments
    const index = comments.findIndex(c => c.id === commentId)
    if (index !== -1) {
      comments.splice(index, 1)
    }
  },
  updateArticlePost: (state: Draft<ComposerState>, action: PayloadAction<{ id: string, article: Partial<ArticleBase> }>) => {
    const { id, article } = action.payload
    const post = state.multiPost.posts[id]
    // Link details are fetched asynchronously and can resolve after the post was deleted.
    if (post) {
      post.article = { ...(post.article as ArticleBase), ...article }
    }
  },
  setMultipostBlueskyFacets: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ postId: string, facets: any[] | undefined }>
  ) => {
    const { postId, facets } = action.payload
    const post = state.multiPost.posts[postId]
    // Facet detection is debounced + async, so it can resolve after the post was deleted.
    if (post) {
      (post.data as ComposerBlueskyPost).facets = facets
    }
  }
}

function setInitialPostState(params: {
  draft: Record<string, BulkContentPost>,
  order: number,
  network: PostDestinationType,
  igLocations: Record<string, LocationInfo | undefined>,
  image?: { url: string, content?: ContentItem },
  article?: { article: ArticleBase, content?: ContentItem },
  file?: File,
  video?: ContentItem,
  videoFile?: { name?: string, url: string, thumbnailUrl?: string },
  profiles?: PostDestination[]
}) {
  const { draft, order, network, igLocations, image, article, file, video, videoFile } = params
  // Stock videos arrive as a ContentItem (not a videoFile), so promote them to a native
  // video here — mirroring the single composer, which converts a stock video to a native
  // upload at add time. Feed VIDEO_TYPE items are deliberately left as content-only
  // (published as video_link), so only STOCK_VIDEO_TYPE copies a native videoUrl.
  const isStockVideo = video?.type === STOCK_VIDEO_TYPE
  const videoUrl = videoFile?.url || (isStockVideo ? (video as Video).videoUrl : undefined)
  const videoFileName = videoFile?.name ? videoFile.name.slice(0, videoFile.name.lastIndexOf('.')) : undefined
  const id = randomId()
  draft[id] = {
    id,
    network,
    imageUrl: image?.url,
    content: image?.content || article?.content || video,
    article: article?.article,
    imageUpload: file ? { file } : undefined,
    order,
    recycle: false,
    data: { status: { html: '', text: '', touched: false } },
    videoUrl,
    videoThumbnailUrl: videoFile?.thumbnailUrl || (isStockVideo ? video?.thumbnailUrl : undefined)
  }
  const articleUrl = article?.article.url

  switch (network) {
    case BRAND_LINKEDIN:
      (draft[id].data as ComposerLinkedinPost).comments = [{ id: randomId(), text: '', delay: 0, html: '' }]
      break
    case BRAND_FACEBOOK:
      (draft[id].data as ComposerFacebookPost).reshare = false
      ; (draft[id].data as ComposerFacebookPost).albums = []
      ; (draft[id].data as ComposerFacebookPost).comments = [{ id: randomId(), text: '', delay: 0, html: '' }]
      break
    case BRAND_TWITTER:
      (draft[id].data as ComposerTwitterPost).tweetType = 'new'
      if (articleUrl) {
        draft[id].data.status = { html: articleUrl, text: articleUrl, touched: false }
      }
      (draft[id].data as ComposerTwitterPost).comments = [{ id: randomId(), text: '', delay: 0, html: '' }]

      break

    case BRAND_THREADS:
      (draft[id].data as ComposerThreadsPost).comments = [{ id: randomId(), text: '', delay: 0, html: '' }]
      break
    case BRAND_PINTEREST:
      (draft[id].data as ComposerPinterestPost).boards = []
      if (video) {
        draft[id].error = {
          message: 'Pinterest does not support video link posts.',
          code: 'composer.errors.pinterest.no-video-links'
        }
      }
      if (article || image?.content) {
        const url = image?.content?.socialLink || article?.content?.socialLink || article?.article.url
        if (url) {
          (draft[id].data as ComposerPinterestPost).destinationUrl = url
        }
      }
      if (videoFileName) {
        (draft[id].data as ComposerPinterestPost).title = videoFileName
      }
      break
    case BRAND_BLUESKY:
      if (articleUrl) {
        draft[id].data.status = { html: articleUrl, text: articleUrl, touched: false }
      }
      (draft[id].data as ComposerBlueskyPost).comments = [{ id: randomId(), text: '', delay: 0, html: '' }]
      break
    case BRAND_YOUTUBE: {
      if (!videoUrl) {
        draft[id].error = {
          message: `${NETWORKS_NAMES[network]} posts require a video file. Please upload a video.`,
          code: `composer.errors.${network}.no-video`
        }
      }
      (draft[id].data as ComposerYoutubePost).comments = [{ id: randomId(), text: '', delay: 0, html: '' }]
      if (videoFileName) {
        (draft[id].data as ComposerYoutubePost).title = videoFileName
      }
      break
    }

    case BRAND_TIKTOK: {
      if (!videoUrl && !image && !file) {
        draft[id].error = {
          message: `TikTok supports only video and image posts. Please add a video file or an image.`,
          code: 'composer.errors.tiktok.no-media-multi'
        }
      }
      if (image) {
        // check if image match TIKTOK_ALLOWED_IMAGE_EXTENSIONS
        const extension = getFileExtensionFromUrl(image.url)
        if (!TIKTOK_ALLOWED_IMAGE_EXTENSIONS.includes(extension as any)) {
          draft[id].error = {
            message: 'TikTok allows only JPEG and WEBP image formats.',
            code: 'composer.errors.tiktok.photo-formats'
          }
        }
      }
      if (videoUrl) {
        const extension = getFileExtensionFromUrl(videoUrl)
        if (!TIKTOK_ALLOWED_VIDEO_FILE_EXTENSIONS.includes(extension as any)) {
          draft[id].error = {
            message: 'TikTok only allows WebM and MP4 files.',
            code: 'composer.errors.tiktok.video-type'
          }
        }
      }
      if (file) {
        const extension = getFileExtensionFromUrl(file.name)
        if (
          !TIKTOK_ALLOWED_VIDEO_FILE_EXTENSIONS.includes(extension as any)
          && !TIKTOK_ALLOWED_IMAGE_EXTENSIONS.includes(extension as any)
        ) {
          draft[id].error = {
            message: 'TikTok only supports JPEG and WEBP formats for images, and MP4 format for videos. '
              + 'Please upload a file in a supported format.',
            code: 'composer.errors.tiktok.file-type'
          }
        }
      }
      (draft[id].data as ComposerTikTokPost).title = ''
      ; (draft[id].data as ComposerTikTokPost).description = ''
      break
    }
    case BRAND_INSTAGRAM:
      (draft[id].data as ComposerInstagramPost).locations = igLocations
      ; (draft[id].data as ComposerInstagramPost).comments = [{ id: randomId(), text: '', delay: 0, html: '' }]
      if (video) {
        draft[id].imageUrl = video.imageUrl
      }
      break
    case BRAND_GOOGLE: {
      const gData = draft[id].data as ComposerGooglePost
      gData.selectedPostType = GOOGLE_POST_TYPE_UPDATE
      gData.update = { link: '' }
      gData.offer = {
        offer: { redeemOnlineUrl: '' },
        event: {
          title: '',
          schedule: { startDate: defaultScheduleTimeString, endDate: defaultGoogleEventEndTime }
        }
      }
      gData.event = {
        title: '',
        schedule: { startDate: defaultScheduleTimeString, endDate: defaultGoogleEventEndTime }
      }
      if (video || videoUrl) {
        draft[id].error = {
          message: 'Google My Business does not support video posts.',
          code: 'composer.errors.google.videos'
        }
      }
      break
    }
  }
}
